import { ChangeDetectionStrategy, Component, effect, inject, input, signal } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { LookupResponse } from '@core/models/common.model';
import { CreateDependantRequest, DependantResponse } from '../../employee.model';
import { LookupStore } from '@core/lookup/lookup.store';import { DEPENDANT_LOOKUP_CODES } from '@core/lookup/lookup-codes';

import { EmployeeStore } from '../../employee.store';
import { DependantModalComponent } from '../dependant-modal/dependant-modal.component';
import { ToastService } from '@core/services/toast.service';

@Component({
  selector: 'employee-dependants-modal',
  imports: [MatDialogModule, ...SharedMaterialUIModule],
  templateUrl: './employee-dependants-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EmployeeDependantsModalComponent {
  private readonly dialogRef = inject(MatDialogRef<EmployeeDependantsModalComponent>);
  private readonly dialog = inject(MatDialog);

  protected readonly employeeStore = inject(EmployeeStore);
  protected readonly lookupStore = inject(LookupStore);
  private readonly toastService = inject(ToastService);

  readonly employeeId = input.required<string>();
  readonly employeeDisplayName = input<string>('');
  readonly canManage = input(false);

  protected readonly actionDependantId = signal<string | null>(null);

  constructor() {
    this.lookupStore.load(DEPENDANT_LOOKUP_CODES);

    effect(() => {
      const employeeId = this.employeeId();
      if (!employeeId) {
        return;
      }

      this.employeeStore.loadDependants(employeeId, true);
    });
  }

  protected close(): void {
    this.dialogRef.close();
  }

  protected refresh(): void {
    this.employeeStore.loadDependants(this.employeeId(), true);
  }

  protected manageDependant(event: {
    dependant?: DependantResponse;
    action: 'create' | 'edit' | 'toggle';
  }): void {
    if (!this.canManage()) {
      return;
    }

    const employeeId = this.employeeId();

    if (event.action === 'toggle' && event.dependant) {
      this.actionDependantId.set(event.dependant.id);

      if (event.dependant.isActive) {
        const complete = () => {
          this.actionDependantId.set(null);
          this.toastService.success('Dependant deactivated.');
        };

        this.employeeStore.deactivateDependant(employeeId, event.dependant.id, complete);
      } else {
        const complete = () => {
          this.actionDependantId.set(null);
          this.toastService.success('Dependant activated.');
        };

        this.employeeStore.activateDependant(employeeId, event.dependant.id, complete);
      }

      return;
    }

    const dialogRef = this.dialog.open(DependantModalComponent, {
      width: '560px',
      maxWidth: '95vw',
    });

    dialogRef.componentRef?.setInput('dependantTypes', this.lookupStore.items('DEPENDANT_TYPE'));
    dialogRef.componentRef?.setInput('titles', this.lookupStore.items('TITLE'));
    dialogRef.componentRef?.setInput('initialValue', event.dependant ?? null);

    dialogRef.afterClosed().subscribe((result: CreateDependantRequest | undefined) => {
      if (!result) {
        return;
      }

      if (event.action === 'create') {
        this.employeeStore.createDependant(employeeId, result, () => {
          this.toastService.success('Dependant created.');
          this.refresh();
        });
        return;
      }

      if (event.action === 'edit' && event.dependant) {
        this.employeeStore.updateDependant(employeeId, event.dependant.id, result, () => {
          this.toastService.success('Dependant updated.');
          this.refresh();
        });
      }
    });
  }

  protected getDependantTypeName(dependantTypeId: string): string {
    return this.resolveLookupName(this.lookupStore.items('DEPENDANT_TYPE'), dependantTypeId);
  }

  protected getTitleName(titleId: string | null): string {
    if (!titleId) {
      return '-';
    }

    return this.resolveLookupName(this.lookupStore.items('TITLE'), titleId);
  }

  private resolveLookupName(items: LookupResponse[], id: string): string {
    const match = items.find((item) => item.id === id);
    if (!match) {
      return id;
    }

    return match.name ?? match.code ?? id;
  }
}
